import * as React from 'react';

import {
  enumProcessingTypeSortOption,
  ProcessingTypeSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  DataGrid,
  ListScreenLayout,
  Pagination,
  SearchField,
  Button,
  Switch,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Plus } from 'lucide-react';

import * as state from '../../_state/processingTypesList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { ProcessingTypeRecord } from '../../types/processingType';

import { useProcessingTypesColumns } from './hooks/useProcessingTypesColumns';
import { ProcessingTypeDialog } from './ProcessingTypeDialog';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: ProcessingTypeSortOption;
    desc: ProcessingTypeSortOption;
  }
> = {
  name: {
    asc: enumProcessingTypeSortOption.NameAsc,
    desc: enumProcessingTypeSortOption.NameDesc,
  },
  code: {
    asc: enumProcessingTypeSortOption.CodeAsc,
    desc: enumProcessingTypeSortOption.CodeDesc,
  },
  createdAt: {
    asc: enumProcessingTypeSortOption.CreatedAtAsc,
    desc: enumProcessingTypeSortOption.CreatedAtDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ PROCESSING TYPES LIST VIEW ⚛️ ⚛️ ⚛️
//
export const ProcessingTypesListView = () => {
  const t = useTranslations();

  // States
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editingProcessingType, setEditingProcessingType] =
    React.useState<ProcessingTypeRecord | null>(null);

  // Atoms
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteProcessingTypes,
  );
  const [searchTerm, setSearchTerm] = useAtom(state.$searchTerm);
  const [showInactive, setShowInactive] = useAtom(state.$showInactive);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  const processingTypes = data?.processingTypes ?? [];
  const total = data?.totalCount ?? 0;

  const emptyPanelProps = {
    title: t.processingTypesView.emptyState.title,
    description: <span>{t.processingTypesView.emptyState.subtitle}</span>,
    centered: true,
  };

  // Callbacks
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumProcessingTypeSortOption.NameAsc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const handleRowClick = React.useCallback(
    ({ row }: { row: ProcessingTypeRecord }) => {
      setEditingProcessingType(row);
      setIsDialogOpen(true);
    },
    [],
  );

  const handleCreateClick = React.useCallback(() => {
    setEditingProcessingType(null);
    setIsDialogOpen(true);
  }, []);

  const handleDialogClose = React.useCallback(() => {
    setIsDialogOpen(false);
    setEditingProcessingType(null);
  }, []);

  const handleDialogSuccess = React.useCallback(() => {
    refetch();
  }, [refetch]);

  // Other data hooks
  const columns = useProcessingTypesColumns({ onRowClick: handleRowClick });

  return (
    <>
      <ListScreenLayout
        filters={
          <>
            <SearchField
              autoFocus
              value={searchTerm ?? ''}
              onChange={(value) => {
                setSearchTerm(value || undefined);
                setOffset(0);
              }}
              placeholder={t.processingTypesView.filters.search}
              className="w-64"
            />
            <div className="flex items-center gap-2">
              <Switch
                isSelected={showInactive ?? false}
                onChange={setShowInactive}
              >
                {t.processingTypesView.filters.showInactive}
              </Switch>
            </div>
          </>
        }
        actions={
          <Button icon={<Plus />} color="primary" onPress={handleCreateClick}>
            {t.processingTypesView.createProcessingType}
          </Button>
        }
        footer={
          total > limit && (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          )
        }
      >
        <DataGrid<ProcessingTypeRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={processingTypes}
          rowKey="id"
          aria-label={t.processingTypesView.table.aria}
          onColumnSort={handleColumnSort}
          className="w-full cursor-pointer text-sm"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.processingTypesView.content.error(
                    error.message,
                  ),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <ProcessingTypeDialog
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        editingProcessingType={editingProcessingType}
      />
    </>
  );
};
